import React, { useState, useRef, useEffect } from 'react';
import prediction from '../../assets/prediction.jpg';
import './Ai.css';
import { BsSend } from "react-icons/bs";
import { AiOutlineCopy, AiOutlineLoading3Quarters, AiOutlineCheck } from "react-icons/ai";
import { LiaThumbsUp, LiaThumbsDown } from "react-icons/lia";
import UnlistedBanner from '../../assets/unlisted-banner.png';
import ReactMarkdown from 'react-markdown'; // Import ReactMarkdown
import Unlisted from '../unlisted/Unlisted';

// Define a type for conversation messages
interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface AIProps {
  ca: string;
  pair: string;
  data: string;
}

const Check = AiOutlineCheck as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const Loading = AiOutlineLoading3Quarters as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const Up = LiaThumbsUp as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const Down = LiaThumbsDown as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const CopyIcon = AiOutlineCopy as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const Send = BsSend as unknown as React.FC<React.SVGProps<SVGSVGElement>>;

const Ai: React.FC<AIProps> = ({ ca, data, pair }) => {
  const [predictImage, setImg] = useState<string>('');
  const [conversation, setConversation] = useState<Message[]>([]);
  const [input, setInput] = useState<string>('');
  const [isTyping, setIsTyping] = useState<boolean>(false);
  // State to track which message index has been copied
  const [copied, setCopied] = useState<{ [key: number]: boolean }>({});

  const conversationContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (conversationContainerRef.current) {
      conversationContainerRef.current.scrollTo({
        top: conversationContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [conversation]);

  useEffect(() => {
    const getPredictionImage = async () => {
      try {
        const res = await fetch(
          `https://jhlgut67phmxt3wypv67jasfba0cnzqj.lambda-url.us-east-1.on.aws/?contract_address=${ca}`
        );

        if (predictImage) {
          URL.revokeObjectURL(predictImage);
        }

        const blob = await res.blob();
        const imgUrl = URL.createObjectURL(blob);
        setImg(imgUrl);
      } catch (e) {
        console.log(e);
      }
    };

    if (ca && predictImage === '' && pair !== '') {
      getPredictionImage();
    } else {
      setImg(UnlistedBanner);
    }
  }, [ca, pair]);

  const handleSend = async () => {
    if (input.trim()) {
      try {
        const userMessage: Message = { role: 'user', content: input.trim() };
        const updatedConversation: Message[] = [...conversation, userMessage];
  
        setConversation(updatedConversation);
        setInput('');
        setIsTyping(true);

        // Updated to send data in the request body as JSON
        const res = await fetch(
          'https://6ya2ziaef4vv7i44pd3hh7dmym0tpgor.lambda-url.us-east-1.on.aws',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              history: JSON.stringify(updatedConversation),
              data: data
            })
          }
        );
  
        const result = await res.json();
  
        const aiResponse: Message = {
          role: 'assistant',
          content: result.content,
        };
        setConversation(prev => [...prev, aiResponse]);
        setIsTyping(false);
      } catch (e) {
        console.log(e);
      }
    }
  };

  const handleCopy = async (text: string, index: number) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(prev => ({ ...prev, [index]: true }));
        setTimeout(() => {
          setCopied(prev => ({ ...prev, [index]: false }));
        }, 3000);
      } catch (err) {
        console.error("Failed to copy text: ", err);
      }
    } else {
      try {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.top = "0";
        textArea.style.left = "0";
        textArea.style.position = "fixed";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
          setCopied(prev => ({ ...prev, [index]: true }));
          setTimeout(() => {
            setCopied(prev => ({ ...prev, [index]: false }));
          }, 3000);
        } else {
          throw new Error("Fallback: Copy command was unsuccessful");
        }
      } catch (err) {
        console.error("Fallback: Oops, unable to copy", err);
      }
    }
  };  

  return (
    <div className="ai-outside">
      <div className="ai-wrapper" ref={conversationContainerRef}>
        <div className="myquestion">
          <p>Predict the future!</p>
        </div>
        <div className="ai-answer">
          {predictImage !== '' ? (
            <img
              className="prediction-image"
              src={predictImage || UnlistedBanner}
              alt="Prediction"
            />
          ) : (
            <div className="ai-answer typing">
              <Loading className="typing-icon" />
            </div>
          )}
        </div>
        {conversation.map((msg, index) => {
          if (msg.role === 'user') {
            // Render user messages as plain text
            return (
              <div key={index} className="myquestion">
                <p>{msg.content}</p>
              </div>
            );
          } else {
            // Render assistant messages with Markdown support and copy functionality
            return (
              <div key={index} className="ai-answer">
                <ReactMarkdown>
                  {msg.content}
                </ReactMarkdown>
                <div className="copy-icons">
                  <span onClick={() => handleCopy(msg.content, index)} style={{ cursor: 'pointer' }}>
                    {copied[index] ? <Check /> : <CopyIcon />}
                  </span>
                  <Up />
                  <Down />
                </div>
              </div>
            );
          }
        })}

        {isTyping && (
          <div className="ai-answer typing">
            <Loading className="typing-icon" />
          </div>
        )}
      </div>

      <div className="question-box">
        <input
          className="ai-question"
          placeholder="Ask questions about this project..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSend();
            }
          }}
        />
        <button className="ai-button" onClick={handleSend}>
          <Send />
        </button>
      </div>
    </div>
  );
};

export default Ai;
